<template>
  <div class="sun-event-editor">
    <div class="sun-event-editor__header">
      <span class="sun-event-editor__title">{{ tt('Events', 'Dogodki') }}</span>
      <button type="button" class="sun-event-editor__add" @click="addEvent">
        <ControlIcon name="plus" size="0.875rem" />
        {{ tt('Add event', 'Dodaj dogodek') }}
      </button>
    </div>

    <p class="sun-event-editor__hint">
      {{
        tt(
          'Events override the base position. Time / sunrise / sunset apply after they pass; high / low brightness apply while the sensor reads past the threshold (sun tracking, when enabled, wins over everything).',
          'Dogodki prevladajo nad osnovnim položajem. Čas / sončni vzhod / sončni zahod se uporabijo, ko minejo; visoka / nizka svetlost velja, dokler senzor bere prek meje (sončno vodenje, ko je omogočeno, prevlada nad vsem).',
        )
      }}
    </p>

    <div v-if="events.length === 0" class="sun-event-editor__empty">
      {{ tt('No events — only the base position applies.', 'Ni dogodkov — velja samo osnovni položaj.') }}
    </div>

    <div v-for="(event, index) in events" :key="index" class="sun-event-editor__row">
      <div class="sun-event-editor__trigger">
        <select class="sun-event-editor__type" :value="event.trigger.type" @change="setType(index, $event)">
          <option v-for="option in TRIGGER_OPTIONS" :key="option.value" :value="option.value">
            {{ sunLabel(option.label) }}
          </option>
        </select>

        <input
          v-if="event.trigger.type === 'time'"
          class="sun-event-editor__time"
          type="time"
          :value="event.trigger.time || '08:00'"
          @change="setTime(index, $event)" />

        <div
          v-else-if="event.trigger.type === 'sunrise' || event.trigger.type === 'sunset'"
          class="sun-event-editor__offset">
          <span>±</span>
          <input
            class="sun-event-editor__offset-input"
            type="number"
            min="-180"
            max="180"
            :value="event.trigger.offset_minutes ?? 0"
            @change="setOffset(index, $event)" />
          <span>min</span>
        </div>

        <div v-else class="sun-event-editor__threshold">
          <input
            class="sun-event-editor__threshold-input"
            type="number"
            min="0"
            step="500"
            :value="event.trigger.threshold_lux ?? 30000"
            @change="setThreshold(index, $event)" />
          <span>lux</span>
        </div>
      </div>

      <div class="sun-event-editor__target">
        <div class="sun-event-editor__slider">
          <span class="sun-event-editor__slider-label">{{ tt('Position', 'Položaj') }}</span>
          <input type="range" min="0" max="100" :value="event.position" @input="setPosition(index, $event)" />
          <span class="sun-event-editor__slider-value"> {{ event.position }}% </span>
        </div>
        <div class="sun-event-editor__slider">
          <span class="sun-event-editor__slider-label">{{ tt('Tilt', 'Naklon') }}</span>
          <input type="range" min="0" max="100" :value="event.tilt" @input="setTilt(index, $event)" />
          <span class="sun-event-editor__slider-value">{{ event.tilt }}%</span>
        </div>
      </div>

      <button
        type="button"
        class="sun-event-editor__remove"
        :title="tt(`Remove event ${index + 1}`, `Odstrani dogodek ${index + 1}`)"
        @click="removeEvent(index)">
        <ControlIcon name="close" size="1rem" />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { tt } from '../../../utils/consoleText';
import ControlIcon from '../../device-icons/ControlIcon.vue';
import {
  SUN_EVENT_TRIGGER_LABELS,
  SunEvent,
  SunEventTrigger,
  SunEventTriggerType,
  sunLabel,
} from '../../../utils/sunTracker';

const props = defineProps<{
  modelValue: SunEvent[];
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: SunEvent[]): void;
}>();

const TRIGGER_OPTIONS = (Object.keys(SUN_EVENT_TRIGGER_LABELS) as SunEventTriggerType[]).map((value) => ({
  value,
  label: SUN_EVENT_TRIGGER_LABELS[value],
}));

const events = computed<SunEvent[]>(() => (Array.isArray(props.modelValue) ? props.modelValue : []));

function commit(next: SunEvent[]) {
  emit('update:modelValue', next);
}

function defaultTrigger(type: SunEventTriggerType): SunEventTrigger {
  if (type === 'time') return { type, time: '08:00' };
  if (type === 'sunrise' || type === 'sunset') {
    return { type, offset_minutes: 0 };
  }
  return { type, threshold_lux: type === 'high_brightness' ? 30000 : 5000 };
}

function addEvent() {
  commit([...events.value, { trigger: defaultTrigger('time'), position: 100, tilt: 0 }]);
}

function removeEvent(index: number) {
  const next = [...events.value];
  next.splice(index, 1);
  commit(next);
}

function mapEvent(index: number, patch: (event: SunEvent) => SunEvent): void {
  commit(events.value.map((event, i) => (i === index ? patch(event) : event)));
}

function setType(index: number, event: Event) {
  const type = (event.target as HTMLSelectElement).value as SunEventTriggerType;
  mapEvent(index, (ev) => ({ ...ev, trigger: defaultTrigger(type) }));
}

function setTime(index: number, event: Event) {
  const time = (event.target as HTMLInputElement).value;
  mapEvent(index, (ev) => ({
    ...ev,
    trigger: { ...ev.trigger, type: 'time' as const, time },
  }));
}

function setOffset(index: number, event: Event) {
  const offset = Number((event.target as HTMLInputElement).value) || 0;
  mapEvent(index, (ev) => ({
    ...ev,
    trigger: { ...ev.trigger, offset_minutes: offset },
  }));
}

function setThreshold(index: number, event: Event) {
  const threshold = Number((event.target as HTMLInputElement).value) || 0;
  mapEvent(index, (ev) => ({
    ...ev,
    trigger: { ...ev.trigger, threshold_lux: threshold },
  }));
}

function setPosition(index: number, event: Event) {
  const position = Number((event.target as HTMLInputElement).value) || 0;
  mapEvent(index, (ev) => ({ ...ev, position }));
}

function setTilt(index: number, event: Event) {
  const tilt = Number((event.target as HTMLInputElement).value) || 0;
  mapEvent(index, (ev) => ({ ...ev, tilt }));
}
</script>

<style scoped>
.sun-event-editor {
  border-top: 1px solid rgba(148, 163, 184, 0.2);
  padding-top: 12px;
  margin-top: 12px;
}

.sun-event-editor__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.sun-event-editor__title {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: rgb(100, 116, 139);
}

.sun-event-editor__add {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 600;
  color: rgb(245, 158, 11);
  background: rgba(245, 158, 11, 0.1);
  border-radius: 999px;
  padding: 5px 10px;
}

.sun-event-editor__hint {
  font-size: 11px;
  line-height: 1.45;
  color: rgb(148, 163, 184);
  margin-top: 6px;
}

.sun-event-editor__empty {
  font-size: 12px;
  color: rgb(148, 163, 184);
  font-style: italic;
  padding: 8px 0;
}

.sun-event-editor__row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-top: 10px;
  padding: 10px;
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.02);
}

.sun-event-editor__trigger {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 150px;
}

.sun-event-editor__type,
.sun-event-editor__time,
.sun-event-editor__offset-input,
.sun-event-editor__threshold-input {
  width: 100%;
  border-radius: 8px;
  border: 1px solid rgba(148, 163, 184, 0.35);
  background: rgba(255, 255, 255, 0.04);
  color: inherit;
  font-size: 12px;
  padding: 5px 8px;
}

.sun-event-editor__offset,
.sun-event-editor__threshold {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: rgb(148, 163, 184);
}

.sun-event-editor__offset-input,
.sun-event-editor__threshold-input {
  width: 84px;
}

.sun-event-editor__target {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.sun-event-editor__slider {
  display: flex;
  align-items: center;
  gap: 8px;
}

.sun-event-editor__slider-label {
  font-size: 11px;
  color: rgb(148, 163, 184);
  width: 44px;
  flex-shrink: 0;
}

.sun-event-editor__slider input[type='range'] {
  flex: 1;
  min-width: 0;
  accent-color: rgb(245, 158, 11);
}

.sun-event-editor__slider-value {
  font-size: 11px;
  font-weight: 600;
  width: 34px;
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.sun-event-editor__remove {
  flex-shrink: 0;
  color: rgb(148, 163, 184);
  border-radius: 999px;
  padding: 4px;
}

.sun-event-editor__remove:hover {
  color: rgb(239, 68, 68);
  background: rgba(239, 68, 68, 0.1);
}
</style>
